/**
 * Event-Driven Scanner API module.
 *
 * Wiz parity: live CloudTrail/EventBridge-style event ingestion, rule matching,
 * and targeted incremental scan triggers. The backend service is mounted under
 * the scan-orchestrator umbrella at /api/v1/event-scanner/*.
 */
import { _unwrap } from './_unwrap';
import { apiClient as fetchClient } from './client';

export interface CloudEvent {
  id: string;
  event_source?: string;
  event_name?: string;
  event_time?: string;
  classification?: 'matched' | 'no_match' | string;
  urgency: number;
  scan_triggered: boolean;
  account_id?: string;
  region?: string;
  processed_at?: string;
}

export interface EventRule {
  id: string;
  event_pattern: string;
  event_source?: string;
  scan_scope: string;
  urgency: number;
  enabled: boolean;
  description?: string;
  created_at?: string;
}

export interface IngestEventRequest {
  event_source?: string;
  event_name?: string;
  event_time?: string;
  affected_resources?: any[];
  account_id?: string;
  region?: string;
  error_code?: string;
  raw_event?: Record<string, any>;
}

export const eventScannerApi = {
  health: async (): Promise<{ status: string; service: string; stage?: number }> => {
    return _unwrap(await fetchClient.get('/api/v1/event-scanner/health'));
  },

  listEvents: async (limit = 50): Promise<{ events: CloudEvent[] }> => {
    const res: any = _unwrap(await fetchClient.get('/api/v1/event-scanner/events', { limit }));
    return { events: res?.events || (Array.isArray(res) ? res : []) };
  },

  listRules: async (): Promise<{ rules: EventRule[] }> => {
    const res: any = _unwrap(await fetchClient.get('/api/v1/event-scanner/rules'));
    return { rules: res?.rules || (Array.isArray(res) ? res : []) };
  },

  ingestEvent: async (event: IngestEventRequest): Promise<{ event_id: string; status: string }> => {
    return _unwrap(await fetchClient.post('/api/v1/event-scanner/ingest', event));
  },

  matchNow: async (): Promise<{ triggered: number }> => {
    const res: any = _unwrap(await fetchClient.post('/api/v1/event-scanner/match-now', {}));
    return { triggered: res?.triggered ?? 0 };
  },
};
